import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { View, Text, Dimensions, ScrollView, StyleSheet, Animated } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AsyncStorage from '@react-native-async-storage/async-storage';
import PaginationDots from '../../components/paginationDots/paginationDots';
import { ImageComp } from '../../components/Image/Image';
import CustomPressable from '../../components/customPressable/customPressable';
import { setShowLoginModal } from '../../Redux/Reducers/appConfigReducer';
import { KEY_HELP, KEY_HELP_PERMISSION} from '../../assets';
import { CHAMBER_KEY, DOOR_KEY } from '../../navigation/StackNavigator/doorKeyNavigator';
import { useCssStyle } from '../../customHook/getCssStyle';
import HeaderWithBack from '../../components/Header/headerWithBack';
import FetchingDataScreen from '../../components/fetchingDataScreen';

const { width } = Dimensions.get('window');
const { width: screenWidth } = Dimensions.get('window');

const DoorKeyGuide = ({ navigation }) => {
  const { isLoggedIn } = useSelector((state) => state.appConfigSlice);
  const { supportedMethods, loading } = useSelector(state => state.doorKeySlice);
  
  const dispatch = useDispatch();
  const getStyles = useCssStyle();
  const scrollViewRef = useRef(null);
  const scrollX = useRef(new Animated.Value(0)).current;
  

  const [activeIndex, setActiveIndex] = useState(0);
  const debounceTimeout = useRef(null);
  const [parentWidth, setParentWidth] = useState(screenWidth);
    
  
  const { t } = useTranslation();

  // Define guide pages for the door key process
  const guidePages = useMemo(() => {
    const secondPageDescription = [];
  
    if (supportedMethods?.some((m) => m.toLowerCase().includes('frontdesk'))) {
      secondPageDescription.push(t('doorkey.verify_identity_front_desk'));
    }
  
    if (supportedMethods?.some((m) => m.toLowerCase().includes('otp'))) {
      secondPageDescription.push(t('doorkey.use_registered_info'));
    }
  
    const pages = [
      {
        image: KEY_HELP_PERMISSION,
        title: t('doorkey.location_and_bluetooth'),
        description: [t('doorkey.enable_bluetooth_and_location')],
      },
      {
        image: KEY_HELP,
        title: t('doorkey.authenticate_identity'),
        description: secondPageDescription,
      },
    ];
  
    return pages;
  }, [t, supportedMethods]);

  useEffect(() => {
    AsyncStorage.setItem('doorKeyGuide', 'true');
  }, []);

  const handleNavigation = useCallback(async () => {
    navigation.replace(DOOR_KEY);
  }, [navigation]);


  const handleNextPress = () => {
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({
        x: (activeIndex + 1) * width,
        animated: true,
      });
      setActiveIndex(prevIndex => prevIndex + 1);
    }
  };
  const handleScroll = (event) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(() => {
      const index = Math.round(offsetX / parentWidth);
      setActiveIndex(index);
    }, 100);
  };

  return (
    <View style={getStyles(['widgetRootContainer', 'doorkeyGuideRootContainer'])}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setParentWidth(width);
      }}>
      <HeaderWithBack navigation={navigation} />
      {loading && <FetchingDataScreen />}

      <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={getStyles(['widgetmainContainer','doorkeyGuideMainContainer'])}
        onLayout={(event) => {
          const { width } = event.nativeEvent.layout;
          setParentWidth(width);
        }}
      >
        <Animated.ScrollView
          ref={scrollViewRef}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScroll={Animated.event(
            [{ nativeEvent: { contentOffset: { x: scrollX } } }],
            { useNativeDriver: false, listener: handleScroll }
          )}
          scrollEventThrottle={16}
          style={{ flexGrow:0 }}
        >
          {guidePages.map((slide, index) => (
            <View key={index} style={getStyles(['widgetmainContainerHeaderContainer', 'doorkeyGuideMainContainerHeaderContainer', { width: parentWidth }])}>
              <ImageComp source={slide.image} style={getStyles(['widgetmainContainerHeaderImg', 'doorkeyGuideMainContainerHeaderImg'])} />

              <View style={[getStyles(['widgetmainContainerHeaderTitleContainer', 'doorkeyGuideMainContainerHeaderTitleContainer'])]}>
                <Text style={getStyles(['widgetmainContainerHeaderTitle', 'doorkeyGuideMainContainerHeaderTitle'])}>{slide.title}</Text>
                <Text style={getStyles(['widgetmainContainerHeaderSubTitle', 'doorkeyGuideMainContainerHeaderSubTitle'])}>{slide.description}</Text>
              </View>
            </View>
          ))}
          
        </Animated.ScrollView>



        <View style={getStyles(['widgetmainContainerChildContainer', 'doorkeyGuideMainContainerChildContainer'])}>
          <PaginationDots
            totalDots={guidePages.length}
            activeIndex={activeIndex}
          />
          <View style={getStyles(['dookeyGuideButtonContainer'])}>
            {activeIndex === guidePages.length - 1 ? null : <CustomPressable
              style={[
                getStyles(['primaryBtn']),
                activeIndex === guidePages.length - 1 && getStyles(['disabledButton']),
              ]}
              onPress={handleNextPress}
              disabled={activeIndex === guidePages.length - 1}>
              <Text style={getStyles(['primaryBtnTxt'])}>
                {t('check_in.next')}
              </Text>
            </CustomPressable>}

            <CustomPressable style={getStyles(['secondaryBtn'])} onPress={handleNavigation}>
              <Text style={getStyles(['secondaryBtnTxt'])}>
                {activeIndex === guidePages.length - 1
                  ? t('doorkey.done')
                  : t('check_in.skip_intro')}
              </Text>
            </CustomPressable>
          </View>

        

        </View>

      </ScrollView>
    </View>
  );
};



export default DoorKeyGuide;
